import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  InputLabel,
  Popover,
  Select,
} from "@mui/material";
import { EmojiEmotions } from "@mui/icons-material";
import TranslateIcon from '@mui/icons-material/Translate';
import { useTheme } from "@emotion/react";
import EmojiPicker from "emoji-picker-react";
import { postApiWithAuth } from "../../utils/api";
import { TRANSLATOR_API } from "../../utils/apiUrl";

const TwilioChatComponent = ({ contactDetail, chatDetail, handleSendTwilioSms, text, setText, translatedText = null, setTranslatedText = null, targetLanguage = null, setTargetLanguage = null, isTranslating = null, setIsTranslating = null }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);


  const isEmojiPickerOpen = Boolean(anchorEl);

  const handleEmojiClick = (emojiObject) => {
    setText((prevMessage) => prevMessage + emojiObject.emoji);
    handleCloseEmojiPicker();
  };

  const handleEmojiIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseEmojiPicker = () => {
    setAnchorEl(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendTwilioSms();
    }
  };

  // Dialog handlers for translation
  const handleOpenDialog = () => {
    setDialogOpen(true);
    setTranslatedText(""); // Reset previous translation
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTranslatedText("");
  };

  // Handle target language change and trigger translation API call
  const handleTargetLanguageChange = async (event) => {
    const newLang = event.target.value;
    setTargetLanguage(newLang);
    if (!text) return;
    setIsTranslating(true);
    try {
      // Replace with your actual API endpoint and adjust as needed
      const response = await postApiWithAuth(TRANSLATOR_API, {
        target_language: newLang,
        text: text
      });
      setTranslatedText(response.data.data.translated_text);
    } catch (error) {
      console.error("Translation error:", error);
      setTranslatedText("Error in translation.");
    } finally {
      setIsTranslating(false);
    }
  };

  // Function to send the translated message
  const handleSendTranslatedMessage = () => {
    handleSendTwilioSms();
    handleCloseDialog();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          border: "1px solid #ddd",
          borderRadius: "8px",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          {chatDetail
            .slice()
            .reverse()
            .map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  flexDirection: msg.type === "outgoing" ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  marginBottom: "16px",
                }}
              >
                {msg.type === "outgoing" ? (
                  msg.agent.pic ? (
                    <Avatar
                      src={msg.agent.pic}
                      alt="Profile Image"
                      sx={{
                        width: 38.71,
                        height: 37.66,
                        marginRight: "8px",
                        marginLeft: "8px",
                        cursor: "pointer",
                      }}
                    />
                  ) : (
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {msg.agent.name ? msg.agent.name.charAt(0) : "A"}
                    </Avatar>
                  )
                ) : (
                  <Avatar sx={{ width: 40, height: 40 }}>
                    {contactDetail?.name ? contactDetail?.name.charAt(0) : "U"}
                  </Avatar>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: msg.type === "outgoing" ? "end" : "start",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ textAlign: "right" }}
                  >
                    {msg.type === "outgoing"
                      ? `${msg?.agent.name}`
                      : `${contactDetail?.name} | ${contactDetail?.number}`}{" "}
                    • {msg.created_at.split("T")[0]}{" "}
                    {msg.created_at.split("T")[1].split(".")[0]}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor:
                        msg.type === "outgoing"
                          ? theme.palette.custom.layoutBgColor
                          : "#F5F5F5",
                      color:
                        msg.type === "outgoing"
                          ? theme.palette.custom.layoutFontColor
                          : "#000",
                      padding: "8px 12px",
                      borderRadius: "12px",
                      marginTop: "4px",
                      whiteSpace: "pre-wrap",
                      width: "650px",
                      fontSize: "14px",
                    }}
                  >
                    {msg.translatedBody &&
                      msg.translatedBody.trim() !== "" &&
                      msg.translatedBody.trim() !== msg.body.trim() && (
                        <Box
                          sx={{
                            backgroundColor: msg.type === "outgoing"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "#ccc",
                            padding: "4px 8px",
                            borderRadius: "8px",
                            fontSize: "13px",
                            marginBottom: "6px",
                          }}
                        >
                          {msg.translatedBody}
                        </Box>
                      )}
                    {msg.body}
                  </Box>
                </Box>
              </Box>
            ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "8px",
            borderTop: "1px solid #ddd",
            gap: "8px",
          }}
        >
          <IconButton onClick={handleEmojiIconClick}>
            <EmojiEmotions />
          </IconButton>
          <IconButton onClick={handleOpenDialog}>
            <TranslateIcon />
          </IconButton>
          <Popover
            open={isEmojiPickerOpen}
            anchorEl={anchorEl}
            onClose={handleCloseEmojiPicker}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </Popover>
          <Box sx={{ position: "relative", flex: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="off"
              size="small"
              placeholder="Type a message"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyPress}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleSendTwilioSms}
            sx={{
              color: theme.palette.custom.layoutFontColor,
              backgroundColor: theme.palette.custom.layoutBgColor,
              width: "160px",
              marginRight: "5%",
              paddingTop: "7.5px",
              paddingBottom: "7.5px",
              "&:hover": {
                backgroundColor: theme.palette.custom.layoutBgColor,
              },
            }}
          >
            Send
          </Button>
        </Box>
      </Box>

      {/* Translation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Translate Message</DialogTitle>
        <DialogContent>
          {/* Source Language */}
          <Typography variant="subtitle2" gutterBottom>
            Source Language: English
          </Typography>
          {/* Original message in a disabled TextField */}
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled
            value={text}
            sx={{ mb: 2 }}
          />
          {/* Target language selection */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="target-language-label">Translate Language</InputLabel>
            <Select
              labelId="target-language-label"
              value={targetLanguage}
              label="Translate Language"
              onChange={handleTargetLanguageChange}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="zh">Chinese</MenuItem>
              <MenuItem value="ru">Russian</MenuItem>
              <MenuItem value="fr">French</MenuItem>
              {/* Add more languages as needed */}
            </Select>
          </FormControl>
          {/* Translated message text field */}
          {isTranslating ? (
            <Typography variant="body2">Translating...</Typography>
          ) : (
            translatedText && (
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                minRows={2}
                value={translatedText}
                sx={{ mb: 2 }}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSendTranslatedMessage} disabled={!translatedText}>
            Send Translate Message
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TwilioChatComponent;
